import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { PlayerEntity } from '../entities/PlayerEntity';
import { COURT } from '../game/constants';
import { ROCKET, WEAPONS } from '../game/weapons';
import { disposeObject3D } from '../utils/dispose';

export interface RocketNetworkState {
  id: number;
  ownerId: string;
  position: number[];
  direction: number[];
  remaining: number;
}

function buildMissile(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'bazooka-missile';
  const steel = new THREE.MeshStandardMaterial({ color: '#a0a8a1', metalness: 0.65, roughness: 0.4 });
  const olive = new THREE.MeshStandardMaterial({ color: '#53683c', roughness: 0.65 });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 0.48, 8), steel);
  group.add(body);
  const warhead: THREE.BufferGeometry[] = [new THREE.ConeGeometry(0.09, 0.23, 8).translate(0, 0.355, 0)];
  for (let i = 0; i < 4; i++) {
    warhead.push(new THREE.BoxGeometry(0.3, 0.16, 0.018).rotateY(i * Math.PI / 2).translate(0, -0.21, 0));
  }
  const geometry = mergeGeometries(warhead);
  if (!geometry) throw new Error('Cannot build missile');
  warhead.forEach(part => part.dispose());
  group.add(new THREE.Mesh(geometry, olive));
  const flame = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.42, 6), new THREE.MeshBasicMaterial({ color: '#ffb32e' }));
  flame.rotation.z = Math.PI;
  flame.position.y = -0.43;
  group.add(flame);
  return group;
}

export class RocketSystem {
  readonly group = new THREE.Group();
  private readonly pool = Array.from({ length: ROCKET.maxActive }, (_, id) => ({
    id, mesh: buildMissile(), active: false, ownerId: '', remaining: 0, trailTime: 0,
    position: new THREE.Vector3(), direction: new THREE.Vector3(),
  }));
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly segment = new THREE.Vector3();
  private readonly offset = new THREE.Vector3();
  private readonly previous = new THREE.Vector3();

  constructor(private readonly players: PlayerEntity[], private readonly events: {
    trail(position: THREE.Vector3): void;
    impact(position: THREE.Vector3, target: PlayerEntity | undefined, ownerId: string): void;
  }) {
    for (const rocket of this.pool) { rocket.mesh.visible = false; this.group.add(rocket.mesh); }
  }

  get canLaunch(): boolean { return this.pool.some(rocket => !rocket.active); }

  launch(player: PlayerEntity): void {
    const rocket = this.pool.find(item => !item.active);
    if (!rocket) return;
    rocket.active = true;
    rocket.ownerId = player.id;
    rocket.remaining = WEAPONS.bazooka.range;
    rocket.trailTime = 0;
    rocket.direction.copy(player.facing).setY(0).normalize();
    rocket.position.copy(player.position).addScaledVector(rocket.direction, 0.9).setY(1.05 + player.airborneY);
    rocket.mesh.position.copy(rocket.position);
    rocket.mesh.quaternion.setFromUnitVectors(this.up, rocket.direction);
    rocket.mesh.visible = true;
  }

  update(delta: number, replica = false): void {
    for (const rocket of this.pool) {
      if (!rocket.active) continue;
      if (!replica) {
        this.previous.copy(rocket.position);
        const step = Math.min(ROCKET.speed * delta, rocket.remaining);
        this.segment.copy(rocket.direction).multiplyScalar(step);
        let fraction = 1;
        let target: PlayerEntity | undefined;
        // Swept sphere entry, choosing the first body rather than player-array order.
        for (const player of this.players) {
          if (player.id === rocket.ownerId || player.eliminated || player.state === 'celebrate') continue;
          this.offset.copy(this.previous).sub(player.position).setY(this.previous.y - player.airborneY - 0.85);
          const a = this.segment.lengthSq();
          const b = this.offset.dot(this.segment);
          const c = this.offset.lengthSq() - 0.72 ** 2;
          const discriminant = b * b - a * c;
          if (discriminant < 0 || a === 0) continue;
          const entry = c <= 0 ? 0 : (-b - Math.sqrt(discriminant)) / a;
          if (entry >= 0 && entry <= fraction) { fraction = entry; target = player; }
        }
        rocket.position.addScaledVector(this.segment, fraction);
        rocket.remaining -= step;
        if (target || rocket.remaining <= 0 || Math.abs(rocket.position.x) >= COURT.halfLength || Math.abs(rocket.position.z) >= COURT.halfWidth) {
          rocket.active = rocket.mesh.visible = false;
          this.events.impact(rocket.position, target, rocket.ownerId);
          continue;
        }
      }
      rocket.mesh.position.copy(rocket.position);
      rocket.trailTime -= delta;
      if (rocket.trailTime <= 0) { this.events.trail(rocket.position); rocket.trailTime = 1 / 60; }
    }
  }

  clear(): void { for (const rocket of this.pool) rocket.active = rocket.mesh.visible = false; }

  networkState(): RocketNetworkState[] {
    return this.pool.filter(r => r.active).map(r => ({ id: r.id, ownerId: r.ownerId,
      position: r.position.toArray(), direction: r.direction.toArray(), remaining: r.remaining }));
  }

  applyNetworkState(states: RocketNetworkState[], extrapolation = 0): void {
    this.clear();
    for (const state of states) {
      const rocket = this.pool[state.id];
      if (!rocket) continue;
      rocket.active = rocket.mesh.visible = true;
      rocket.ownerId = state.ownerId;
      rocket.remaining = state.remaining;
      rocket.position.fromArray(state.position);
      rocket.direction.fromArray(state.direction);
      rocket.position.addScaledVector(rocket.direction, Math.min(state.remaining, ROCKET.speed * extrapolation));
      rocket.mesh.position.copy(rocket.position);
      rocket.mesh.quaternion.setFromUnitVectors(this.up, rocket.direction);
    }
  }

  dispose(): void { for (const rocket of this.pool) disposeObject3D(rocket.mesh); this.group.clear(); }
}
